import { useEffect, useState } from "react";
import config from "../../config";

// Every hook here exposes `pending`: "no data for what's on screen yet". Once
// data exists, a refetch (run finished, re-score, run switch) keeps the old
// data visible instead of flashing spinners.

// Cached per (reportId, runNumber, include) tuple for the lifetime of the page.
const cache = new Map();
const key = (rid, n, inc) => `${rid}::${n}::${inc || "full"}`;

// Drop cached run payloads (after a re-score rewrites them server-side).
export function clearRunCache() { cache.clear(); }

export function useRunData(reportId, runNumber, { includeAggregationsOnly } = {}) {
  const [entry, setEntry] = useState(null); // { run, data }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!runNumber) return;
    const k = key(reportId, runNumber, includeAggregationsOnly ? "agg" : "full");
    if (cache.has(k)) { setEntry({ run: runNumber, data: cache.get(k) }); return; }

    let cancelled = false;
    setLoading(true); setError("");
    const qs = includeAggregationsOnly ? "?include=aggregations" : "";
    fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}/runs/${runNumber}${qs}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.success) { cache.set(k, d.run); setEntry({ run: runNumber, data: d.run }); }
        else setError(d.error || "Failed to load run.");
      })
      .catch(() => { if (!cancelled) setError("Network error."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportId, runNumber, includeAggregationsOnly]);

  const data = entry && entry.run === runNumber ? entry.data : null;
  return { data, loading, error, pending: !!runNumber && !data && !error };
}

// One filtered page of a run's responses, served by the API so the browser
// never downloads the whole run. `filters` = { model, mentioned, prompt, competitor, q }.
export function useRunPage(reportId, runNumber, filters, page, pageSize = 50) {
  const [state, setState] = useState(null); // { run, results, total, facets }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  for (const [k, v] of Object.entries(filters || {})) {
    if (v && v !== "all") params.set(k, String(v));
  }
  const qs = params.toString();

  useEffect(() => {
    if (!runNumber) return;
    let cancelled = false;
    setLoading(true); setError("");
    fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}/runs/${runNumber}?${qs}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.success) {
          setState({ run: runNumber, results: d.results || [], total: d.total || 0, facets: d.facets || null });
        } else setError(d.error || "Failed to load responses.");
      })
      .catch(() => { if (!cancelled) setError("Network error."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportId, runNumber, qs]);

  const current = state && state.run === runNumber ? state : null;
  return {
    results: current ? current.results : null,
    total: current ? current.total : 0,
    facets: current ? current.facets : null,
    loading, error,
    pending: !!runNumber && !current && !error,
  };
}

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Per-run slices (rates, byModel, byPrompt, competitors, cited domains) for
// every completed run, oldest first. Re-fetched when `refreshKey` changes —
// pass report.updatedAt so a finished run or a re-score refreshes it.
export function useTrends(reportId, refreshKey) {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    setLoading(true); setError("");
    fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}/trends`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.success) setRuns(d.runs || []);
        else setError(d.error || "Failed to load trends.");
      })
      .catch(() => { if (!cancelled) setError("Network error."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportId, refreshKey]);

  return { runs: runs || [], loading, error, pending: runs === null && !error };
}

// Run picker over completed runs only; errored runs have no data to show.
export function useRunPicker(report) {
  const runs = (report?.runs || []).filter((r) => r.status === "completed");
  const latest = runs.length ? runs[runs.length - 1].runNumber : null;
  const [runNumber, setRunNumber] = useState(latest);
  useEffect(() => { if (latest != null && runNumber == null) setRunNumber(latest); }, [latest, runNumber]);
  return [runNumber, setRunNumber, runs];
}
